// frontend/src/pages/cargo/QuotationsList.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';

// ─────────────────────────────────────────────────────────────
// Async polling config
// ─────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 3000;     // every 3s
const MAX_POLL_ATTEMPTS = 40;      // ~2 minutes

export default function QuotationsList({ idfData, onSelect }) {
    const [quotations, setQuotations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedQuote, setSelectedQuote] = useState(null);
    const hasAutoSelected = useRef(false);
    const mounted = useRef(true);

    // ── NEW: async polling state ──────────────────────────────
    const [polling, setPolling] = useState(false);
    const [pollAttempt, setPollAttempt] = useState(0);
    const [waitingMessage, setWaitingMessage] = useState('');
    const pollTimerRef = useRef(null);

    // Auto-generate quotations when component mounts
    useEffect(() => {
        mounted.current = true;
        if (idfData?.body) {
            console.log('[QuotationsList] Component mounted with idfData:', idfData);
            fetchQuotations();
        } else {
            console.log('[QuotationsList] No idfData available:', idfData);
            setError('No IDF data available. Please go back and complete the cover creation.');
        }
        return () => {
            mounted.current = false;
            hasAutoSelected.current = false;
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        };
        // eslint-disable-next-line
    }, [idfData]);

    // ─────────────────────────────────────────────────────────
    // Extract a quotes array from any response shape
    // (kept identical to the original logic)
    // ─────────────────────────────────────────────────────────
    const extractQuotes = (data) => {
        let quotes = [];

        if (data && typeof data === 'object') {
            if (Array.isArray(data)) {
                quotes = data;
            } else if (data.quotations && Array.isArray(data.quotations)) {
                quotes = data.quotations;
            } else if (data.body && data.body.quotation) {
                quotes = data.body.quotation;
            } else if (data.body && data.body.quotations) {
                quotes = data.body.quotations;
            } else if (data.data && data.data.quotations) {
                quotes = data.data.quotations;
            } else if (data.quotation) {
                quotes = data.quotation;
            } else if (data.data && Array.isArray(data.data)) {
                quotes = data.data;
            } else {
                const values = Object.values(data);
                if (values.some(v => v && typeof v === 'object' && v.totalCharges !== undefined)) {
                    quotes = values;
                }
            }
        }

        if (!Array.isArray(quotes)) quotes = [];

        return quotes;
    };

    // ─────────────────────────────────────────────────────────
    // Filter to Mayfair-only (kept identical)
    // ─────────────────────────────────────────────────────────
    const filterMayfair = (quotes) => {
        return quotes.filter(quote =>
            quote &&
            typeof quote === 'object' &&
            (
                quote.name?.toLowerCase().includes('mayfair') ||
                quote.insuranceCompanyKraPin === 'P051178573M' ||
                quote.insuranceId === 4 ||
                quote.insuranceId === '4'
            )
        );
    };

    // ─────────────────────────────────────────────────────────
    // Apply a filtered set of quotes to state
    // (extracted so both sync + async paths can reuse it)
    // ─────────────────────────────────────────────────────────
    const applyQuotes = (filteredQuotes) => {
        if (!mounted.current) return;

        if (filteredQuotes.length === 0) {
            setError('No MAYFAIR INSURANCE quotations available for this IDF.');
            setQuotations([]);
        } else {
            setQuotations(filteredQuotes);
            // Auto-select the first quote ONLY ONCE
            if (!hasAutoSelected.current) {
                console.log('[QuotationsList] Auto-selecting quote:', filteredQuotes[0]);
                hasAutoSelected.current = true;
                setSelectedQuote(0);
                onSelect(filteredQuotes[0], 0);
            }
        }
    };

    // ─────────────────────────────────────────────────────────
    // Poll GET /cargo/quotations/:idfNumber
    // ─────────────────────────────────────────────────────────
    const startPolling = useCallback((idfNumber, attemptNumber) => {
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current);

        pollTimerRef.current = setTimeout(async () => {
            if (!mounted.current) return;

            try {
                const { data } = await api.get(
                    `/cargo/quotations/${encodeURIComponent(idfNumber)}`
                );

                if (!mounted.current) return;

                console.log('[QuotationsList] Poll attempt', attemptNumber + 1, '→', data);

                // The backend returns { idfNumber, ready, quotations: [...] }
                const rows =
                    Array.isArray(data?.quotations) ? data.quotations :
                        Array.isArray(data) ? data : [];

                if (rows.length > 0) {
                    const filtered = filterMayfair(rows);
                    setPolling(false);
                    setPollAttempt(0);
                    setWaitingMessage('');
                    applyQuotes(filtered);
                    return;
                }

                const nextAttempt = attemptNumber + 1;
                setPollAttempt(nextAttempt);

                if (nextAttempt >= MAX_POLL_ATTEMPTS) {
                    setPolling(false);
                    setError(
                        'Quotations are taking longer than expected. Please refresh or contact support.'
                    );
                    return;
                }

                startPolling(idfNumber, nextAttempt);
            } catch (err) {
                if (!mounted.current) return;

                const nextAttempt = attemptNumber + 1;
                setPollAttempt(nextAttempt);

                if (nextAttempt >= MAX_POLL_ATTEMPTS) {
                    setPolling(false);
                    setError(
                        err.response?.data?.error ||
                        'Failed to fetch quotations. Please try again.'
                    );
                    return;
                }

                startPolling(idfNumber, nextAttempt);
            }
        }, POLL_INTERVAL_MS);
        // eslint-disable-next-line
    }, []);

    // ─────────────────────────────────────────────────────────
    // Fetch quotations — now handles both sync + async
    // ─────────────────────────────────────────────────────────
    const fetchQuotations = async () => {
        if (loading || polling) return;
        setLoading(true);
        setError('');
        setPolling(false);
        setPollAttempt(0);
        setWaitingMessage('');

        try {
            if (!idfData || !idfData.body) {
                throw new Error('IDF data is missing or invalid');
            }

            const idfNumber =
                idfData.body.idf_number ||
                idfData.body.sadId ||
                idfData.body.sad_id ||
                idfData.idf_number;

            console.log('[QuotationsList] Fetching quotations for IDF:', idfNumber);

            const { data } = await api.post('/cargo/quotations', idfData.body);
            console.log('[QuotationsList] Raw API response:', data);

            // ── Detect async acknowledgement ─────────────────
            const isAsync =
                data?.mode === 'async' ||
                data?.accepted === true ||
                (!data?.quotations && !data?.body?.quotation && data?.idfNumber);

            if (isAsync) {
                console.log('[QuotationsList] IRA accepted async request. Starting polling.');
                if (!mounted.current) return;

                setWaitingMessage(
                    data.message ||
                    'The IRA portal is generating quotations for your shipment. This usually takes 30–60 seconds.'
                );
                setPolling(true);
                setPollAttempt(0);
                setLoading(false);
                startPolling(idfNumber, 0);
                return;
            }

            // ── Sync path — unchanged ────────────────────────
            const quotes = extractQuotes(data);
            console.log('[QuotationsList] Extracted quotes before filter:', quotes.length);

            const filteredQuotes = filterMayfair(quotes);
            console.log('[QuotationsList] Filtered quotes:', filteredQuotes.length);

            if (!mounted.current) return;

            if (filteredQuotes.length === 0) {
                // Check if the backend explicitly said "not ready yet"
                if (data?.ready === false || data?.pending === true) {
                    setWaitingMessage(
                        data.message ||
                        'Waiting for IRA to deliver quotations…'
                    );
                    setPolling(true);
                    setPollAttempt(0);
                    setLoading(false);
                    startPolling(idfNumber, 0);
                    return;
                }

                setError('No MAYFAIR INSURANCE quotations available for this IDF.');
                setQuotations([]);
            } else {
                applyQuotes(filteredQuotes);
            }
        } catch (err) {
            console.error('[QuotationsList] Error fetching quotations:', err);

            // 202 Accepted → async — start polling
            if (err.response?.status === 202) {
                if (!mounted.current) return;
                const idfNumber =
                    idfData?.body?.idf_number ||
                    idfData?.body?.sadId ||
                    idfData?.body?.sad_id;

                setWaitingMessage(
                    err.response?.data?.message ||
                    'The IRA portal is processing your request. Please wait…'
                );
                setPolling(true);
                setPollAttempt(0);
                setLoading(false);
                startPolling(idfNumber, 0);
                return;
            }

            if (mounted.current) {
                setError(
                    err.response?.data?.error ||
                    err.message ||
                    'Failed to fetch quotations. Please try again.'
                );
            }
        } finally {
            if (mounted.current) {
                setLoading(false);
            }
        }
    };

    const formatKES = (amount) => {
        if (!amount && amount !== 0) return 'KES 0.00';
        return 'KES ' + Number(amount).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const handleSelectQuote = (quote, idx) => {
        console.log('[QuotationsList] Quote selected manually:', quote);
        setSelectedQuote(idx);
        onSelect(quote, idx);
    };

    // ═════════════════════════════════════════════════════════
    // LOADING
    // ═════════════════════════════════════════════════════════
    if (loading) {
        return (
            <div className="max-w-3xl mx-auto animate-fadeIn space-y-6">
                <div className="text-center space-y-3">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] shadow-2xl shadow-[#3a663f]/25 mb-3">
                        <div className="w-10 h-10 text-white animate-spin-slow">
                            <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </div>
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-800 dark:text-white">Finding Your Quote...</h2>
                    <p className="text-gray-500 dark:text-slate-400">Please wait while we fetch your insurance rate</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-slate-700 animate-pulse">
                    <div className="flex items-start justify-between mb-6">
                        <div className="space-y-3 flex-1">
                            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/4" />
                            <div className="h-7 bg-gray-200 dark:bg-slate-700 rounded w-2/3" />
                        </div>
                        <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded-full w-28" />
                    </div>
                    <div className="h-20 bg-gray-100 dark:bg-slate-700 rounded-xl mb-6" />
                    <div className="grid grid-cols-2 gap-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-12 bg-gray-100 dark:bg-slate-700 rounded-lg" />
                        ))}
                    </div>
                </div>
                <style>{`
                    .animate-spin-slow {
                        animation: spin 2s linear infinite;
                    }
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // ═════════════════════════════════════════════════════════
    // NEW: POLLING / WAITING STATE
    // ═════════════════════════════════════════════════════════
    if (polling) {
        return (
            <div className="max-w-3xl mx-auto animate-fadeIn space-y-6">
                <div className="text-center space-y-3">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 shadow-2xl shadow-amber-500/25 mb-3">
                        <div className="w-10 h-10 text-white animate-spin-slow">
                            <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-800 dark:text-white">
                        Waiting for IRA Quotations
                    </h2>
                    <p className="text-gray-500 dark:text-slate-400 max-w-md mx-auto">
                        {waitingMessage ||
                            'The IRA portal is generating quotations for your shipment.'}
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-full text-xs font-medium border border-amber-200 dark:border-amber-800">
                        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                        Attempt {pollAttempt + 1} of {MAX_POLL_ATTEMPTS}
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-slate-700 animate-pulse">
                    <div className="flex items-start justify-between mb-6">
                        <div className="space-y-3 flex-1">
                            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/4" />
                            <div className="h-7 bg-gray-200 dark:bg-slate-700 rounded w-2/3" />
                        </div>
                        <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded-full w-28" />
                    </div>
                    <div className="h-20 bg-gray-100 dark:bg-slate-700 rounded-xl mb-6" />
                    <div className="grid grid-cols-2 gap-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-12 bg-gray-100 dark:bg-slate-700 rounded-lg" />
                        ))}
                    </div>
                </div>

                <div className="text-center">
                    <button
                        onClick={() => {
                            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
                            setPolling(false);
                            fetchQuotations();
                        }}
                        className="text-sm text-[#3a663f] dark:text-green-400 hover:underline font-medium"
                    >
                        Retry now
                    </button>
                </div>

                <style>{`
                    .animate-spin-slow {
                        animation: spin 2s linear infinite;
                    }
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // ═════════════════════════════════════════════════════════
    // ERROR
    // ═════════════════════════════════════════════════════════
    if (error) {
        return (
            <div className="max-w-2xl mx-auto text-center py-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50 dark:bg-red-900/20 mb-4">
                    <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">Unable to Load Quotation</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-2 max-w-sm mx-auto">{error}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                    <button
                        onClick={fetchQuotations}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-[#3a663f] text-white font-semibold rounded-xl hover:bg-[#2d5232] transition-colors shadow-md hover:shadow-lg"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Retry Now
                    </button>
                    <button
                        onClick={() => window.history.back()}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    const quote = quotations.length > 0 ? quotations[0] : null;

    // ═════════════════════════════════════════════════════════
    // EMPTY
    // ═════════════════════════════════════════════════════════
    if (!quote && !loading) {
        return (
            <div className="max-w-2xl mx-auto text-center py-16 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 dark:bg-slate-700 mb-4">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">No Quotation Available</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-2 max-w-sm mx-auto">
                    No MAYFAIR INSURANCE quotation is available for this IDF number.
                </p>
                <button
                    onClick={fetchQuotations}
                    className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 bg-[#3a663f] text-white font-semibold rounded-xl hover:bg-[#2d5232] transition-colors shadow-md hover:shadow-lg"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                </button>
            </div>
        );
    }

    // ═════════════════════════════════════════════════════════
    // MAIN (unchanged)
    // ═════════════════════════════════════════════════════════
    return (
        <div className="max-w-3xl mx-auto animate-fadeIn space-y-6">
            {/* Header */}
            <div className="text-center space-y-3">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] shadow-2xl shadow-[#3a663f]/25 mb-3">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                </div>
                <h2 className="text-3xl font-extrabold text-gray-800 dark:text-white tracking-tight">
                    Your Quotation
                </h2>
                <p className="text-gray-500 dark:text-slate-400 max-w-md mx-auto">
                    Review your MAYFAIR INSURANCE quote
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full text-xs font-medium border border-green-200 dark:border-green-800">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Quote Ready for Review
                </div>
            </div>

            {/* Single Quote Card */}
            {quote && (
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                    {/* Premium Header */}
                    <div className="relative overflow-hidden bg-gradient-to-r from-[#2d5232] to-[#3a663f] px-6 py-8 text-white">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div>
                                <p className="text-white/80 text-sm uppercase tracking-wider">Total Premium</p>
                                <p className="text-4xl font-extrabold">{formatKES(quote.totalCharges)}</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl text-center">
                                    <p className="text-[10px] uppercase tracking-wider text-white/70">Basic Premium</p>
                                    <p className="text-lg font-bold">{formatKES(quote.basicPremium)}</p>
                                </div>
                                <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl text-center">
                                    <p className="text-[10px] uppercase tracking-wider text-white/70">Stamp Duty</p>
                                    <p className="text-lg font-bold">{formatKES(quote.stampDuty)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Insurer Info */}
                    <div className="px-6 py-5 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/30">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-[#3a663f]/10 dark:bg-[#3a663f]/20 flex items-center justify-center flex-shrink-0">
                                    <svg className="w-6 h-6 text-[#3a663f] dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg text-gray-800 dark:text-white">
                                        {quote.name || 'MAYFAIR INSURANCE COMPANY LIMITED'}
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-slate-400">
                                        KRA PIN: {quote.insuranceCompanyKraPin || 'P051178573M'}
                                    </p>
                                </div>
                            </div>
                            <span className="bg-[#3a663f]/10 dark:bg-[#3a663f]/20 text-[#3a663f] dark:text-green-400 text-xs font-semibold px-3 py-1.5 rounded-full border border-[#3a663f]/20">
                                MAYFAIR INSURANCE
                            </span>
                        </div>
                    </div>

                    {/* Premium Breakdown */}
                    <div className="px-6 py-5">
                        <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-slate-500 mb-4">Premium Breakdown</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 text-center border border-gray-100 dark:border-slate-600">
                                <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-slate-500">Training Levy</p>
                                <p className="text-sm font-semibold text-gray-800 dark:text-white mt-1">{formatKES(quote.trainingLevy)}</p>
                            </div>
                            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 text-center border border-gray-100 dark:border-slate-600">
                                <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-slate-500">PHCF</p>
                                <p className="text-sm font-semibold text-gray-800 dark:text-white mt-1">{formatKES(quote.phcf)}</p>
                            </div>
                            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 text-center border border-gray-100 dark:border-slate-600">
                                <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-slate-500">MCI</p>
                                <p className="text-sm font-semibold text-gray-800 dark:text-white mt-1">{formatKES(quote.mci)}</p>
                            </div>
                            <div className="bg-[#3a663f]/5 dark:bg-[#3a663f]/10 rounded-xl p-4 text-center border border-[#3a663f]/20 dark:border-[#3a663f]/30">
                                <p className="text-[10px] uppercase tracking-wider text-[#3a663f] dark:text-green-400">Total</p>
                                <p className="text-sm font-bold text-[#3a663f] dark:text-green-400 mt-1">{formatKES(quote.totalCharges)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Items Covered */}
                    {quote.items && quote.items.length > 0 && (
                        <div className="px-6 pb-5">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-slate-500">
                                    Items Covered ({quote.items.length})
                                </p>
                            </div>
                            <div className="bg-gray-50 dark:bg-slate-700/30 rounded-xl border border-gray-100 dark:border-slate-600 overflow-hidden">
                                <div className="max-h-36 overflow-y-auto">
                                    {quote.items.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className={`flex items-center justify-between p-3 text-sm hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors ${idx !== quote.items.length - 1 ? 'border-b border-gray-100 dark:border-slate-600' : ''
                                                }`}
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <span className="w-6 h-6 rounded-full bg-[#3a663f]/10 dark:bg-[#3a663f]/20 flex items-center justify-center text-[10px] font-bold text-[#3a663f] dark:text-green-400 flex-shrink-0">
                                                    {idx + 1}
                                                </span>
                                                <span className="truncate text-gray-700 dark:text-gray-300">
                                                    {item.tariffGoodsDesc || item.tariff_goods_desc || `Item ${idx + 1}`}
                                                </span>
                                            </div>
                                            <span className="font-medium text-gray-800 dark:text-white whitespace-nowrap ml-4">
                                                {formatKES(item.premium)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="px-6 py-5 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/30">
                        <div className="flex flex-col sm:flex-row items-center gap-4">
                            <button
                                onClick={() => handleSelectQuote(quote, 0)}
                                className="w-full sm:flex-1 bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-semibold py-3.5 rounded-xl shadow-lg shadow-[#3a663f]/20 hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 transform active:scale-[0.98]"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {selectedQuote === 0 ? '✓ Quote Selected' : 'Select This Quote'}
                            </button>
                            <button
                                onClick={fetchQuotations}
                                className="w-full sm:w-auto px-6 py-3.5 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl border border-gray-300 dark:border-slate-600 transition-all duration-200 flex items-center justify-center gap-2 hover:shadow-md"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Refresh
                            </button>
                        </div>

                        {selectedQuote === 0 && (
                            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-green-600 dark:text-green-400 animate-fadeIn">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>Quote selected! You can now proceed to the next step.</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Footer */}
            {quote && (
                <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-400 dark:text-slate-500 pt-4">
                    <span>Quote provided by MAYFAIR INSURANCE COMPANY LIMITED</span>
                    <span className="w-px h-3 bg-gray-300 dark:bg-slate-600 hidden sm:block"></span>
                    <span>IDF: {idfData?.body?.idf_number || 'N/A'}</span>
                </div>
            )}
        </div>
    );
}